<script setup lang="ts" generic="F extends string">
/**
 * The one sort control (MAR-948, wave 2; guard `tests/unit/viewControlsGuard.test.ts`).
 *
 * An icon trigger and two radio groups — the field and the direction — so each option says which is
 * in force (`aria-checked`) rather than only showing a tick. The caller owns the state and where it
 * lives (the documents store refetches, the clients browser re-sorts in memory); this only asks.
 *
 * Not in `ui/`: it knows its own i18n strings, which `ui/` primitives never do.
 */
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-vue-next'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu'

export type SortDirection = 'asc' | 'desc'

const props = withDefaults(defineProps<{
  options: readonly { field: F; label: string }[]
  /** The field in force. Pass the EFFECTIVE one, so no option is ticked that the list is not sorted by. */
  field: F
  order: SortDirection
  /** `xs` for the sidebar rail's 28px row, `sm` for a 32px toolbar. */
  size?: 'xs' | 'sm'
}>(), { size: 'sm' })

const emit = defineEmits<{ sort: [field: F, order: SortDirection] }>()

const { t } = useI18n()
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <button
        type="button"
        class="flex shrink-0 items-center justify-center rounded-md border border-input bg-background transition-colors hover:bg-accent-lighter"
        :class="size === 'xs' ? 'h-7 w-7' : 'h-8 w-8'"
        :title="t('common.sortBy')"
        :aria-label="t('common.sortBy')"
      >
        <ArrowUpDown class="h-3.5 w-3.5" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" class="min-w-[190px]">
      <DropdownMenuLabel>{{ t('common.sortBy') }}</DropdownMenuLabel>
      <DropdownMenuRadioGroup :model-value="field" @update:model-value="(f) => emit('sort', f as F, order)">
        <DropdownMenuRadioItem v-for="opt in props.options" :key="opt.field" :value="opt.field" class="text-xs">
          {{ opt.label }}
        </DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
      <DropdownMenuSeparator />
      <DropdownMenuRadioGroup :model-value="order" @update:model-value="(o) => emit('sort', field, o as SortDirection)">
        <DropdownMenuRadioItem value="asc" class="text-xs">
          <ArrowUp class="h-3.5 w-3.5 text-muted-foreground" />
          {{ t('common.sortAscending') }}
        </DropdownMenuRadioItem>
        <DropdownMenuRadioItem value="desc" class="text-xs">
          <ArrowDown class="h-3.5 w-3.5 text-muted-foreground" />
          {{ t('common.sortDescending') }}
        </DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
